/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { AppUtils } from '../../../core/AppUtils';
import { CursorManager } from '../../../core/navigation';
import { RootState } from '../../../store/root.reducers';
import { disableGhostMode } from '../../../store/guide/guidehighlight/guidehighlight.reducers';
import { GuideHighlightProps } from './GuideHighlightProps';
import { CellTypes } from '../ProgramCell/CellTypes';

import './GuideHighlight.scss';

const GuideHighlight: FunctionComponent<GuideHighlightProps> = ({ id, cellType }): JSX.Element => {
    const [isActive, setIsActive] = useState(false);
    const { verticalPosition, horizontalPosition, ghostMode } = useSelector(
        (state: RootState) => state.guideHighlight,
    );
    const { isTurboScrollActive, isGuidePageScrollActive } = useSelector(
        (state: RootState) => state.turboScroll,
    );
    const currentId = AppUtils.createHighlightId(horizontalPosition, verticalPosition);

    useEffect(() => {
        if (id === currentId) {
            setIsActive(true);
        } else {
            setIsActive(false);
        }
    }, [verticalPosition, horizontalPosition]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!CursorManager.isCursorAppeared()) {
            dispatch(disableGhostMode());
        }
    }, [CursorManager.isCursorAppeared()]);

    const { isOverlayDialogActive: isAnyGuideOverlayVisible } = useSelector(
        (state: RootState) => state.overlayDialog,
    );
    const { isPastGuideMode } = useSelector((state: RootState) => state.gridGuide);
    const { isSpinnerActive } = useSelector((state: RootState) => state.spinner);

    const isTurboModeActive = isTurboScrollActive || isGuidePageScrollActive;
    const isGhostHighlightActive = ghostMode && id === currentId;
    const isHighlightVisible = !(isAnyGuideOverlayVisible || isSpinnerActive || isTurboModeActive);

    return (
        <React.Fragment>
            {isActive ? (
                <div
                    className={`highlight${isGhostHighlightActive ? ' ghost' : ''}${
                        cellType === CellTypes.FUTURE_CONTINUOUS && !isPastGuideMode
                            ? ' hide-right-border'
                            : ''
                    }${isHighlightVisible ? '' : ' hidden'} 
                    ${
                        cellType === CellTypes.PAST_CONTINUOUS && isPastGuideMode
                            ? ' hide-left-border'
                            : ''
                    }`}
                    id={id}
                    data-testid="guide-highlight"
                ></div>
            ) : null}
        </React.Fragment>
    );
};

GuideHighlight.propTypes = {
    id: PropTypes.string,
    cellType: PropTypes.string,
};
export default GuideHighlight;
